import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'
import type { AuthenticatedRequest } from '@app/rest/rest.lib'

import { usePagination } from '../pagination/pagination.hooks'
import { usePatientsSearch } from '../patient/patient.hooks'
import {
  revokeRemoteInteraction,
  sendInteractionToIntakeAnywhere,
} from '../patient/patient.rest'

import {
  addPatientsToProgram,
  assertGetProgramInteractionHistoryForPatientSuccessResponse,
  deleteProgramEnrolment,
  getProgram as getProgramFromServer,
  getPrograms,
  getProgramInteractionHistoryForPatient,
  getReasonsForDiscontinuation,
  updateProgramEnrolmentStatus,
  getProgramEnrolments,
  getProgramStatistics,
  addReportTemplateToProgramInteraction,
  deleteReportTemplateFromProgramInteraction,
  createProgramInteraction,
  deleteProgramInteraction as deleteProgramInteractionHttpRequest,
  updateProgramInteraction as updateProgramInteractionHttpRequest,
  updateProgramInteractionProperties as updateProgramInteractionPropertiesHttpRequest,
  removeProgramInteractionHttpRequest,
  uploadBulkEnrolmentFile,
  updateProgramEnrolmentStartDate,
  createProgramInteractionWithDefinitionId,
  updateProgramInteractionWithDefinitionIdHttpRequest,
  listInteractionRecordsForProgram,
} from './program.rest'

import type {
  IUseProgram,
  IProgram,
  IUseListPrograms,
  IProgramInteractionId,
  IUseProgramInteractionHistory,
  IProgramId,
  IGetProgramEnrolmentOptions,
  IProgramEnrolment,
  ProgramEnrolmentStatusId,
  IUseProgramEnrolments,
  IUseProgramEnrolmentsWithPatients,
  IUseProgramStatistics,
  ProgramInteractionTypeId,
  IUsePatientProgramInteractionRecord,
  IProgramInteractionRecord,
} from './program.interface'
import type { IPatientId } from '../patient/patient.interface'

export function useListPrograms(): IUseListPrograms {
  const [programs, setPrograms] = useState<IProgram[]>([])
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalNumberOfRecords, setTotalNumberOfRecords] = useState<number>(0)

  const [isLoading, setIsLoading] = useState(true)
  const requestor = useAuthenticatedRequestor()

  const fetchPrograms = useCallback(
    async (pageNumber?: number, recordsPerPage?: number) => {
      setIsLoading(true)
      const response = await requestor(getPrograms(pageNumber, recordsPerPage))

      if (response.status === 200) {
        setPrograms(response.data.programs)
        setPageCount(response.data.pageCount)
        setTotalNumberOfRecords(response.data.totalNumberOfRecords)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }

      setIsLoading(false)
    },
    [requestor],
  )

  return {
    programs,
    isLoading,
    pageCount,
    totalNumberOfRecords,
    getPrograms: fetchPrograms,
  }
}

export function useProgram(programId?: string): IUseProgram {
  const [program, setProgram] = useState<IProgram | undefined>(undefined)
  const [errorMessage, setError] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(true)
  const requestor = useAuthenticatedRequestor()

  const fetchProgram = useCallback(async () => {
    setIsLoading(true)
    setError(undefined)

    try {
      const response = await requestor(getProgramFromServer(programId))

      if (response.status === 200) {
        setProgram(response.data.program)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    } catch {
      setError('We could not load the program. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [programId, requestor])

  useEffect(() => {
    if (programId) {
      fetchProgram()
    }
  }, [fetchProgram, programId])

  const clearError = () => {
    setError(undefined)
  }

  const makeRequestAndUpdateProgram = useCallback(
    async <ResponseData extends object>(
      request: AuthenticatedRequest<ResponseData>,
    ): Promise<ResponseData | null> => {
      setIsLoading(true)
      setError(undefined)

      try {
        const response = await requestor(request)

        if (response.status === 200) {
          await fetchProgram()
          return response.data
        } else {
          throw new Error(
            `Server returned a non-200 response: ${response.status}`,
          )
        }
      } catch {
        setError('We could not process your request. Please try again.')
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [fetchProgram, requestor],
  )

  const addReportTemplate = useCallback(
    async (
      programInteractionId: string,
      templateId: string,
      label?: string,
    ) => {
      await makeRequestAndUpdateProgram(
        addReportTemplateToProgramInteraction(
          programInteractionId,
          templateId,
          label,
        ),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  const deleteReportTemplate = useCallback(
    async (programInteractionId: string, templateId: string) => {
      makeRequestAndUpdateProgram(
        deleteReportTemplateFromProgramInteraction(
          programInteractionId,
          templateId,
        ),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  const addProgramInteraction = useCallback(
    async (programInteractionType: string, instrumentIds: string[]) => {
      await makeRequestAndUpdateProgram(
        createProgramInteraction(
          programId,
          programInteractionType,
          instrumentIds,
        ),
      )
    },
    [makeRequestAndUpdateProgram, programId],
  )

  const deleteProgramInteraction = useCallback(
    async (programInteractionId: string) => {
      await makeRequestAndUpdateProgram(
        deleteProgramInteractionHttpRequest(programInteractionId),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  const removeProgramInteraction = useCallback(
    async (programInteractionId: string) => {
      await makeRequestAndUpdateProgram(
        removeProgramInteractionHttpRequest(programInteractionId),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  const updateProgramInteraction = useCallback(
    async (programInteractionId: string, instrumentIds: string[]) => {
      await makeRequestAndUpdateProgram(
        updateProgramInteractionHttpRequest(
          programInteractionId,
          instrumentIds,
        ),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  const updateProgramInteractionProperties = useCallback(
    async (
      programInteractionId: string,
      label?: string,
      daysAfterInitialInteraction?: number,
    ) => {
      await makeRequestAndUpdateProgram(
        updateProgramInteractionPropertiesHttpRequest(
          programInteractionId,
          label,
          daysAfterInitialInteraction,
        ),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  const addProgramInteractionWithDefinitionId = useCallback(
    async (
      interactionType: ProgramInteractionTypeId,
      visitDefinitionId: string,
    ) => {
      await makeRequestAndUpdateProgram(
        createProgramInteractionWithDefinitionId(
          programId,
          interactionType,
          visitDefinitionId,
        ),
      )
    },
    [makeRequestAndUpdateProgram, programId],
  )

  const updateProgramInteractionWithDefinitionId = useCallback(
    async (programInteractionId: string, visitDefinitionId: string) => {
      await makeRequestAndUpdateProgram(
        updateProgramInteractionWithDefinitionIdHttpRequest(
          programInteractionId,
          visitDefinitionId,
        ),
      )
    },
    [makeRequestAndUpdateProgram],
  )

  return {
    program,
    isLoading,
    errorMessage,
    clearError,
    addReportTemplate,
    deleteReportTemplate,
    addProgramInteraction,
    addProgramInteractionWithDefinitionId,
    deleteProgramInteraction,
    updateProgramInteraction,
    updateProgramInteractionWithDefinitionId,
    removeProgramInteraction,
    updateProgramInteractionProperties,
  }
}

export function useProgramInteractionHistory(): IUseProgramInteractionHistory {
  const [programInteractionHistory, setProgramInteractionHistory] =
    useState<Record<IProgramInteractionId, Date>>(null)

  const [isLoading, setIsLoading] = useState(true)
  const requestor = useAuthenticatedRequestor()

  const getProgramInteractionHistory = useCallback(
    async (patientId: string) => {
      setIsLoading(true)
      const response = await requestor(
        getProgramInteractionHistoryForPatient(patientId),
      )

      if (response.status === 200) {
        assertGetProgramInteractionHistoryForPatientSuccessResponse(
          response.data,
        )
        setProgramInteractionHistory(response.data.history)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }

      setIsLoading(false)
    },
    [requestor],
  )

  return {
    getProgramInteractionHistory,
    programInteractionHistory,
    isLoading,
  }
}

interface IUseReasonsForDiscontinuation {
  reasons: string[]
}

export function useReasonsForDiscontinuation(): IUseReasonsForDiscontinuation {
  const requestor = useAuthenticatedRequestor()
  const [reasons, setReasons] = useState<string[]>([])

  const callServer = useCallback(async () => {
    const response = await requestor(getReasonsForDiscontinuation())
    if (response.status === 200) {
      setReasons(response.data.reasons)
    } else {
      throw new Error(
        `server returned returned a non 200 response '${response.status}'`,
      )
    }
  }, [requestor])

  useEffect(() => {
    callServer()
  }, [callServer])

  return {
    reasons,
  }
}

export function useProgramEnrolments(
  programId?: IProgramId,
  defaultSearchOptions?: Partial<IGetProgramEnrolmentOptions>,
): IUseProgramEnrolments {
  const [programEnrolments, setProgramEnrolments] = useState<
    IProgramEnrolment[]
  >([])

  const [isLoading, setIsLoading] = useState(true)

  const {
    pageCount,
    setPageCount,
    totalNumberOfRecords,
    setTotalNumberOfRecords,
    pageNumber,
    setPageNumber,
    recordsPerPage,
    setRecordsPerPage,
  } = usePagination()

  const [searchOptions, setSearchOptions] = useState<
    Partial<IGetProgramEnrolmentOptions>
  >({})

  const requestor = useAuthenticatedRequestor()

  const addPatientsToProgramOnServer = useMemo(() => {
    return async (
      programId: IProgramId,
      patientIds: IPatientId[],
      startDate: string,
    ) => {
      const response = await requestor(
        addPatientsToProgram(programId, patientIds, startDate),
      )
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          `server returned a non 200 response '${response.status}'`,
        )
      }
    }
  }, [requestor])

  const deletePatientsFromProgramOnServer = useMemo(() => {
    return async (programId: IProgramId, patientId: IPatientId) => {
      const response = await requestor(
        deleteProgramEnrolment(programId, patientId),
      )
      if (response.status !== 200) {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    }
  }, [requestor])

  const fetchProgramEnrolments = useCallback(async () => {
    const options = {
      ...defaultSearchOptions,
      ...searchOptions,
    }

    setIsLoading(true)
    const response = await requestor(
      getProgramEnrolments(programId, pageNumber, recordsPerPage, options),
    )

    if (response.status === 200) {
      setProgramEnrolments(response.data.enrolments)
      setPageCount(response.data.pageCount)
      setTotalNumberOfRecords(response.data.totalNumberOfRecords)
    } else {
      throw new Error(
        `server returned returned a non 200 response '${response.status}'`,
      )
    }

    setIsLoading(false)
  }, [
    defaultSearchOptions,
    pageNumber,
    programId,
    recordsPerPage,
    requestor,
    searchOptions,
    setPageCount,
    setTotalNumberOfRecords,
  ])

  const updateEnrolmentStatusOnServer = useCallback(
    async (
      programId: IProgramId,
      patientIds: IPatientId[],
      status: ProgramEnrolmentStatusId,
      statusReason?: string,
      statusReasonDescription?: string,
    ) => {
      const response = await requestor(
        updateProgramEnrolmentStatus(
          programId,
          patientIds,
          status,
          statusReason,
          statusReasonDescription,
        ),
      )
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    },
    [requestor],
  )

  const handleAddPatientsToProgram = useCallback(
    async (patientIds: IPatientId[], startDate: string) => {
      if (programId) {
        await addPatientsToProgramOnServer(programId, patientIds, startDate)
        await fetchProgramEnrolments()
      }
    },
    [addPatientsToProgramOnServer, fetchProgramEnrolments, programId],
  )

  const handleDeletePatientsFromProgram = useCallback(
    async (patientId: IPatientId) => {
      if (programId) {
        await deletePatientsFromProgramOnServer(programId, patientId)
        await fetchProgramEnrolments()
      }
    },
    [deletePatientsFromProgramOnServer, fetchProgramEnrolments, programId],
  )

  const handleUpdateEnrolmentStatus = useCallback(
    async (
      patientIds: IPatientId[],
      status: ProgramEnrolmentStatusId,
      statusReason?: string,
      statusReasonDescription?: string,
    ) => {
      if (programId) {
        await updateEnrolmentStatusOnServer(
          programId,
          patientIds,
          status,
          statusReason,
          statusReasonDescription,
        )
        await fetchProgramEnrolments()
      }
    },
    [fetchProgramEnrolments, programId, updateEnrolmentStatusOnServer],
  )

  useEffect(() => {
    fetchProgramEnrolments()
  }, [fetchProgramEnrolments])

  return {
    addPatientsToProgram: handleAddPatientsToProgram,
    deletePatientsFromProgram: handleDeletePatientsFromProgram,
    getProgramEnrolments: fetchProgramEnrolments,
    isLoading,
    pageCount,
    pageNumber,
    programEnrolments,
    recordsPerPage,
    searchOptions,
    setRecordsPerPage,
    setPageNumber,
    setSearchOptions,
    totalNumberOfRecords,
    updateEnrolmentStatus: handleUpdateEnrolmentStatus,
  }
}

interface IUseUpdateEnrolmentStartDate {
  updateStartDate: (
    patientId: IPatientId,
    startDate: string,
  ) => Promise<IProgramEnrolment | null>
  isLoading: boolean
  error?: string
}

export function useUpdateEnrolmentStartDate(
  programId: IProgramId,
): IUseUpdateEnrolmentStartDate {
  const requestor = useAuthenticatedRequestor()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()

  const updateStartDate = useCallback(
    async (
      patientId: IPatientId,
      startDate: string,
    ): Promise<IProgramEnrolment | null> => {
      setIsLoading(true)
      setError(undefined)

      try {
        const response = await requestor(
          updateProgramEnrolmentStartDate(programId, patientId, startDate),
        )

        if (response.status === 200) {
          return response.data.enrolment
        } else {
          throw new Error(
            `Server returned a non-200 response: ${response.status}`,
          )
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [programId, requestor],
  )

  return {
    updateStartDate,
    isLoading,
    error,
  }
}

interface IUseBulkEnrolment {
  clearStatus: () => void
  isLoading: boolean
  numberEnrolled?: number
  numberUpdated?: number
  invalidMRNs: string[]
  uploadEnrolmentFile: (file: File) => void
  enrolmentErrors: { type: string; rowIndex: number; mrn?: string }[]
  uploadStatus?: 'success' | 'failure'
  uploadError?: string
}

export function useBulkEnrolment(programId: string): IUseBulkEnrolment {
  const requestor = useAuthenticatedRequestor()
  const [isLoading, setIsLoading] = useState(false)
  const [numberEnrolled, setNumberEnrolled] = useState<number | undefined>(
    undefined,
  )
  const [numberUpdated, setNumberUpdated] = useState<number | undefined>(
    undefined,
  )
  const [invalidMRNs, setInvalidMRNs] = useState<string[]>([])
  const [uploadStatus, setUploadStatus] = useState<
    'success' | 'failure' | undefined
  >(undefined)
  const [uploadError, setUploadError] = useState<string | undefined>(undefined)
  const [enrolmentErrors, setEnrolmentErrors] = useState<
    { type: string; rowIndex: number; mrn?: string }[]
  >([])

  const clearStatus = useCallback(() => {
    setIsLoading(false)
    setNumberEnrolled(undefined)
    setNumberUpdated(undefined)
    setInvalidMRNs([])
    setUploadStatus(undefined)
    setUploadError(undefined)
  }, [])

  const uploadEnrolmentFile = useCallback(
    async (file: File) => {
      setIsLoading(true)
      clearStatus()

      try {
        const response = await requestor(
          uploadBulkEnrolmentFile(file, programId),
        )

        if (response.status === 200) {
          setIsLoading(false)
          setNumberEnrolled(response.data.numberEnrolled)
          setNumberUpdated(response.data.numberUpdated)
          setEnrolmentErrors(response.data.errors)
          setUploadStatus('success')
        } else {
          throw new Error(
            `Server returned a non-200 response: ${response.status}`,
          )
        }
      } catch (error) {
        setIsLoading(false)
        setUploadStatus('failure')
        setUploadError(
          'An error occurred while uploading the file. Please try again.',
        )
      }
    },
    [clearStatus, requestor, programId],
  )

  return {
    clearStatus,
    isLoading,
    numberEnrolled,
    numberUpdated,
    invalidMRNs,
    uploadEnrolmentFile,
    uploadStatus,
    uploadError,
    enrolmentErrors,
  }
}

export function useProgramEnrolmentsWithPatients(
  programId?: IProgramId,
  defaultSearchOptions?: Partial<IGetProgramEnrolmentOptions>,
): IUseProgramEnrolmentsWithPatients {
  const { patients, getPatients } = usePatientsSearch()

  const {
    addPatientsToProgram,
    deletePatientsFromProgram,
    getProgramEnrolments,
    pageNumber,
    recordsPerPage,
    searchOptions,
    setPageNumber,
    setRecordsPerPage,
    setSearchOptions,
    updateEnrolmentStatus,
    ...patientEnrolments
  } = useProgramEnrolments(programId, defaultSearchOptions)

  const handleGetPatients = useCallback(async () => {
    try {
      if (programId) {
        await getPatients(pageNumber, recordsPerPage, {
          ...searchOptions,
          inProgramId: programId,
          notInProgramId: undefined,
        })
      }
    } catch (error) {
      console.error(error)
    }
  }, [getPatients, pageNumber, programId, recordsPerPage, searchOptions])

  const handleAddPatientsToProgram = useCallback(
    async (patients: IPatientId[], startDate: string) => {
      try {
        await addPatientsToProgram(patients, startDate)
        await handleGetPatients()
      } catch (error) {
        console.error(error)
      }
    },
    [addPatientsToProgram, handleGetPatients],
  )

  const handleDeletePatientsFromProgram = useCallback(
    async (patientId: IPatientId) => {
      try {
        await deletePatientsFromProgram(patientId)
        await handleGetPatients()
      } catch (error) {
        console.error(error)
      }
    },
    [deletePatientsFromProgram, handleGetPatients],
  )

  const handleUpdateEnrolmentStatus = useCallback(
    async (
      patientIds: IPatientId[],
      status: ProgramEnrolmentStatusId,
      statusReason?: string,
      statusReasonDescription?: string,
    ) => {
      await updateEnrolmentStatus(
        patientIds,
        status,
        statusReason,
        statusReasonDescription,
      )
      handleGetPatients()
    },
    [handleGetPatients, updateEnrolmentStatus],
  )

  useEffect(() => {
    handleGetPatients()
  }, [handleGetPatients])

  return {
    ...patientEnrolments,
    addPatientsToProgram: handleAddPatientsToProgram,
    deletePatientsFromProgram: handleDeletePatientsFromProgram,
    getPatients: handleGetPatients,
    getProgramEnrolments,
    pageNumber,
    patients: patients.filter(
      (patient) =>
        patientEnrolments.programEnrolments.find(
          (pe) => pe.patientId === patient.id,
        ) !== undefined,
    ),
    recordsPerPage,
    searchOptions,
    setRecordsPerPage,
    setPageNumber,
    setSearchOptions,
    updateEnrolmentStatus: handleUpdateEnrolmentStatus,
  }
}

export function useProgramInteractionRecords(): IUsePatientProgramInteractionRecord {
  const [interactions, setInteractions] = useState<IProgramInteractionRecord[]>(
    [],
  )
  const [isLoading, setIsLoading] = useState(true)
  const [total, setTotal] = useState(0)

  const requestor = useAuthenticatedRequestor()

  const fetchProgramInteractionRecords = useCallback(
    async (
      programId: IProgramId,
      pageNumber: number,
      recordsPerPage: number,
      patientName?: string,
      mrn?: string,
      isDeliverable?: boolean,
      status?: string,
      sortField?: string,
      sortDirection?: string,
    ) => {
      setIsLoading(true)
      try {
        const response = await requestor(
          listInteractionRecordsForProgram(
            programId,
            pageNumber,
            recordsPerPage,
            {
              patientName,
              mrn,
              isDeliverable,
              status,
              sortField,
              sortDirection,
            },
          ),
        )
        if (response.status === 200) {
          setInteractions(response.data.interactions || [])
          setTotal(response.data.total || 0)
        } else {
          console.error(
            `Error fetching configured interactions: ${response.status}`,
          )
        }
      } catch (error) {
        console.error(`Error fetching configured interactions: ${error}`)
      } finally {
        setIsLoading(false)
      }
    },
    [requestor],
  )

  const handleRevokeRemoteInteraction = useCallback(
    async (
      patientId: string,
      programInteractionId: string,
      programId: string,
      pageNumber: number,
      recordsPerPage: number,
      patientName?: string,
      mrn?: string,
      isDeliverable?: boolean,
      status?: string,
      sortField?: string,
      sortDirection?: string,
    ) => {
      const response = await requestor(
        revokeRemoteInteraction(patientId, programInteractionId),
      )
      if (response.status === 200) {
        fetchProgramInteractionRecords(
          programId,
          pageNumber,
          recordsPerPage,
          patientName,
          mrn,
          isDeliverable,
          status,
          sortField,
          sortDirection,
        )
      } else {
        console.error(`Error revoking remote interaction: ${response.status}`)
      }
    },
    [requestor, fetchProgramInteractionRecords],
  )

  const handleSendInteractionToIntakeAnywhere = useCallback(
    async (
      patientId: string,
      programInteractionId: string,
      programId: string,
      pageNumber: number,
      recordsPerPage: number,
      patientName?: string,
      mrn?: string,
      isDeliverable?: boolean,
      status?: string,
      sortField?: string,
      sortDirection?: string,
    ) => {
      const response = await requestor(
        sendInteractionToIntakeAnywhere(patientId, programInteractionId),
      )
      if (response.status === 200) {
        fetchProgramInteractionRecords(
          programId,
          pageNumber,
          recordsPerPage,
          patientName,
          mrn,
          isDeliverable,
          status,
          sortField,
          sortDirection,
        )
      } else {
        console.error(`Error transmitting interaction: ${response.status}`)
      }
    },
    [requestor, fetchProgramInteractionRecords],
  )

  return {
    interactions,
    isLoading,
    fetchProgramInteractionRecords,
    sendInteractionToIntakeAnywhere: handleSendInteractionToIntakeAnywhere,
    revokeRemoteInteraction: handleRevokeRemoteInteraction,
    total,
  }
}

export function useProgramStatistics(): IUseProgramStatistics {
  const [statistics, setStatistics] = useState<{
    enrolledPatients: number
    completedInteractions: number
    pendingInteractions: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const requestor = useAuthenticatedRequestor()

  const fetchStatistics = useCallback(
    async (programId: IProgramId) => {
      setIsLoading(true)
      try {
        const response = await requestor(getProgramStatistics(programId))
        if (response.status === 200) {
          setStatistics(response.data)
        } else {
          console.error(`Error fetching statistics: ${response.status}`)
        }
      } catch (error) {
        console.error('Error fetching program statistics', error)
      } finally {
        setIsLoading(false)
      }
    },
    [requestor],
  )

  return {
    statistics,
    isLoading,
    fetchStatistics,
  }
}
